import { computed, Ref, watch } from 'vue';
import { useContext } from '@nuxtjs/composition-api';
import { SanityDocument } from '@sanity/client';
import { ILookupParams } from '~/store/documents/types';
import { SanityDocumentSchema } from '~/types/sanity-documents';
import { TDocumentState } from '~/plugins/sanityLookup/types';

interface ILookupListParams extends ILookupParams {
  _type: SanityDocumentSchema | SanityDocumentSchema[];
}

type TLookupType = 'fetchDocument' | 'fetchReference';

const isSingleTon = (
  data: SanityDocumentSchema | SanityDocumentSchema[],
): data is SanityDocumentSchema => typeof data === 'string';

export const useSanityDocumentList = <
  TDoc extends SanityDocument = SanityDocument,
>(
  params: Ref<ILookupListParams>,
  withReferences = false,
) => {
  const { $sanityLookup } = useContext();

  const lookupType = computed<TLookupType>(() => {
    if (isSingleTon(params.value._type) && params.value.slug?.length) {
      return 'fetchDocument';
    }
    return 'fetchReference';
  });

  const singleSchema = computed(() =>
    isSingleTon(params.value._type) ? params.value._type : undefined,
  );
  const slugs = computed(() =>
    !params.value.slug?.length
      ? []
      : typeof params.value.slug === 'string'
      ? [params.value.slug]
      : params.value.slug,
  );
  const ids = computed(() =>
    !params.value._id?.length
      ? []
      : typeof params.value._id === 'string'
      ? [params.value._id]
      : params.value._id,
  );

  const getter = (
    type: TLookupType,
    key: string,
    schema?: SanityDocumentSchema,
  ) => {
    if (type === 'fetchDocument' && schema) {
      return () =>
        $sanityLookup.getDocument<TDoc>({
          _type: schema,
          slug: key,
        });
    } else if (type === 'fetchReference') {
      return () =>
        $sanityLookup.getDocumentFromReference<TDoc>({
          _ref: key,
        });
    }
  };

  const fetcher = (
    type: TLookupType,
    key: string[],
    schema?: SanityDocumentSchema,
  ) => {
    if (type === 'fetchDocument' && schema) {
      return () =>
        $sanityLookup.fetchDocument({
          _type: schema,
          slug: key,
          fetchReferences: withReferences,
        });
    } else if (type === 'fetchReference') {
      return () =>
        $sanityLookup.fetchReference({
          _id: key,
          _type: params.value._type,
          fetchReferences: withReferences,
        });
    }
  };

  const docState = computed(() => {
    const states: Array<TDocumentState<TDoc>> = [];
    const keys = lookupType.value === 'fetchDocument' ? slugs.value : ids.value;
    keys?.forEach((key) => {
      const getState = getter?.(lookupType.value, key, singleSchema.value);
      if (!getState) {
        return;
      }
      const state = getState();
      if (state) {
        states.push(state);
      }
    });
    return states;
  });

  const fetchDocuments = () => {
    const keys = lookupType.value === 'fetchDocument' ? slugs.value : ids.value;
    const fetchDocs = fetcher?.(lookupType.value, keys, singleSchema.value);
    return fetchDocs?.();
  };

  watch(
    params,
    (newParams, oldParams) => {
      if (
        !(
          newParams._id?.length ||
          (newParams.slug?.length && isSingleTon(newParams._type))
        )
      ) {
        return;
      }
      if (
        newParams._id !== oldParams?._id ||
        newParams._type !== oldParams?._type ||
        newParams.slug !== oldParams?.slug
      ) {
        fetchDocuments();
      }
    },
    { immediate: true },
  );

  return {
    docs: computed(() => docState.value),
    hasErrorAny: computed(
      () => !!docState.value?.find((state) => !!state?.hasError),
    ),
    isLoadingAny: computed(
      () => !!docState.value?.find((state) => !!state?.isLoading),
    ),
    fetchDocuments,
  };
};
